import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import api from '../../services/api';
import { AttackType, AttackTypeCreateRequest, AttackTypeUpdateRequest } from '../../types/attacks';
import { convertLegacyTargets } from '../../utils/targetUtils';
import { useTimezone } from '../../contexts/TimezoneContext';
import { formatUiDateOnly } from '../../utils/dateUtils';
import { detailSurfaceSx } from '../../utils/surfaces';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attack-types-tabpanel-${index}`}
      aria-labelledby={`attack-types-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AttackTypes: React.FC = () => {
  const { timezone } = useTimezone();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const { selectedTenant } = useTenant();
  const [attackTypes, setAttackTypes] = useState<AttackType[]>([]);
  const [allAttackTypes, setAllAttackTypes] = useState<AttackType[]>([]); // All attack types for ID validation
  const [tenants, setTenants] = useState<Array<{id: string, name: string}>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [filterClass, setFilterClass] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedAttackType, setSelectedAttackType] = useState<AttackType | null>(null);
  const [createStep, setCreateStep] = useState(1);
  const [attackIdError, setAttackIdError] = useState<string | null>(null);
  
  // Sort state - default to sorting by attack_id
  type SortField = 'attack_id' | 'name' | 'attack_class' | 'scope' | 'created_at';
  const [sortField, setSortField] = useState<SortField>('attack_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [createForm, setCreateForm] = useState<AttackTypeCreateRequest>({
    name: '',
    description: '',
    attack_class: 'application',
    attack_numeric_id: 150,
    config_schema: {
      type: 'object',
      properties: {
        targets: { type: 'array', items: { type: 'string' }, minItems: 1 }
      },
      required: ['targets']
    },
    scope: 'tenant'
  });

  // Function to find next available attack ID based on scope
  const findNextAvailableId = (existingAttackTypes: AttackType[], scope: 'tenant' | 'global' = 'tenant'): number => {
    const usedIds = new Set<number>();
    
    // Determine the ID range based on scope
    const minId = scope === 'global' ? 1 : 150;
    const maxId = scope === 'global' ? 149 : 999;
    
    // Extract numeric IDs from existing attack types in the relevant range
    existingAttackTypes.forEach(attackType => {
      if (attackType.id.startsWith('attack_id_')) {
        const numericId = parseInt(attackType.id.split('_')[2]);
        if (!isNaN(numericId) && numericId >= minId && numericId <= maxId) {
          usedIds.add(numericId);
        }
      }
    });
    
    // Find the first available ID in the appropriate range
    for (let id = minId; id <= maxId; id++) {
      if (!usedIds.has(id)) {
        return id;
      }
    }
    
    // If all IDs are taken, return minId as fallback
    return minId;
  };

  // Function to validate attack ID based on scope
  const validateAttackId = (id: number, scope: 'tenant' | 'global' = 'tenant'): string | null => {
    // Determine the ID range based on scope
    const minId = scope === 'global' ? 1 : 150;
    const maxId = scope === 'global' ? 149 : 999;
    
    if (isNaN(id) || id < minId || id > maxId) {
      return `Attack ID must be between ${minId} and ${maxId} for ${scope} attack types`;
    }
    
    // Check if ID is already used across ALL attack types (not just filtered ones)
    const usedIds = new Set<number>();
    allAttackTypes.forEach(attackType => {
      if (attackType.id.startsWith('attack_id_')) {
        const numericId = parseInt(attackType.id.split('_')[2]);
        if (!isNaN(numericId) && numericId >= minId && numericId <= maxId) {
          usedIds.add(numericId);
        }
      }
    });
    
    if (usedIds.has(id)) {
      return 'This Attack ID is already in use. Please choose a different one.';
    }
    
    return null;
  };

  const [editForm, setEditForm] = useState<AttackTypeUpdateRequest>({
    name: '',
    description: '',
    attack_class: 'application',
    config_schema: {
      type: 'object',
      properties: {
        targets: { type: 'array', items: { type: 'string' }, minItems: 1 }
      },
      required: ['targets']
    }
  });

  // Form data for schema-based form rendering (reusing existing pattern)
  const [createFormData, setCreateFormData] = useState<Record<string, any>>({});
  const [editFormData, setEditFormData] = useState<Record<string, any>>({});

  // Optional manual JSON schema editing (advanced).
  const [createSchemaMode, setCreateSchemaMode] = useState<'generated' | 'manual'>('generated');
  const [createSchemaText, setCreateSchemaText] = useState<string>('');
  const [createSchemaError, setCreateSchemaError] = useState<string | null>(null);
  const [editSchemaMode, setEditSchemaMode] = useState<'generated' | 'manual'>('generated');
  const [editSchemaText, setEditSchemaText] = useState<string>('');
  const [editSchemaError, setEditSchemaError] = useState<string | null>(null);

  const isSuperAdmin = permissions?.user.is_admin || false;
  const canCreate = isSuperAdmin || permissions?.tenant_permissions.some(t => t.role === 'admin') || false;

  // Pre-defined field templates for different attack classes
  const fieldTemplates = {
    application: [
      { name: 'targets', type: 'array', title: 'Target URLs', description: 'List of target URLs to attack', required: true, items: { type: 'string' }, minItems: 1 },
      { name: 'threads', type: 'integer', title: 'Thread Count', description: 'Number of concurrent threads', required: true, minimum: 1, maximum: 10000, default: 50 },
      { name: 'timeout', type: 'integer', title: 'HTTP Timeout', description: 'HTTP request timeout in seconds', required: false, minimum: 1, maximum: 300, default: 20 },
      { name: 'http_version', type: 'string', title: 'HTTP Version', description: 'HTTP protocol version', required: false, enum: ['1.0', '1.1', '2.0'], default: '1.1' },
      { name: 'tls_fingerprint', type: 'string', title: 'TLS Fingerprint (JA3/JA4)', description: "Impersonate a real browser's TLS/HTTP2 fingerprint (curl_cffi) to evade JA3/JA4 bot detection such as Cloudflare. 'disabled' keeps the default Python fingerprint.", required: false, enum: ['disabled', 'chrome', 'chrome124', 'chrome131', 'firefox', 'safari', 'safari17_0', 'edge'], default: 'disabled' },
      { name: 'user_agents', type: 'array', title: 'User Agents', description: 'List of user agent strings', required: false, items: { type: 'string' } }
    ],
    infrastructure: [
      { name: 'targets', type: 'array', title: 'Target Hosts', description: 'List of target hosts/IPs to attack', required: true, items: { type: 'string' }, minItems: 1 },
      { name: 'port', type: 'integer', title: 'Port', description: 'Target port number', required: true, minimum: 1, maximum: 65535, default: 80 },
      { name: 'protocol', type: 'string', title: 'Protocol', description: 'Network protocol to use', required: true, enum: ['tcp', 'udp', 'icmp'], default: 'tcp' },
      { name: 'attack_interval', type: 'integer', title: 'Attack Interval', description: 'Interval between attacks in milliseconds', required: false, minimum: 1, maximum: 1000, default: 100 }
    ],
    volumetric: [
      { name: 'targets', type: 'array', title: 'Target Hosts', description: 'List of target hosts/IPs to attack', required: true, items: { type: 'string' }, minItems: 1 },
      { name: 'data_size', type: 'integer', title: 'Data Size', description: 'Size of data packets in bytes', required: true, minimum: 1, maximum: 65507, default: 1024 },
      { name: 'protocol', type: 'string', title: 'Protocol', description: 'Network protocol to use', required: true, enum: ['udp', 'tcp'], default: 'udp' },
      { name: 'attack_interval', type: 'integer', title: 'Attack Interval', description: 'Interval between attacks in milliseconds', required: false, minimum: 1, maximum: 1000, default: 100 }
    ]
  };

  // State for managing field configuration
  const [selectedFields, setSelectedFields] = useState<Record<string, any>>({});
  const [customFields, setCustomFields] = useState<Array<{name: string, type: string, title: string, description: string, required: boolean, config: any}>>([]);
  
  // State for edit field configuration
  const [editSelectedFields, setEditSelectedFields] = useState<Record<string, any>>({});
  const [editCustomFields, setEditCustomFields] = useState<Array<{name: string, type: string, title: string, description: string, required: boolean, config: any}>>([]);

  const loadAttackTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const scope = tabValue === 0 ? undefined : tabValue === 1 ? 'tenant' : 'global';
      const data = await api.getAttackTypes(filterClass || undefined, scope);
      setAttackTypes(data);
      
      // Fetch ALL attack types (without scope filter) to find the next available ID
      // This ensures we check against all IDs in the database, not just the filtered ones
      const allData = await api.getAttackTypes(filterClass || undefined, undefined);
      setAllAttackTypes(allData); // Store for validation
      const nextId = findNextAvailableId(allData, createForm.scope || 'tenant');
      setCreateForm(prev => ({
        ...prev,
        attack_numeric_id: nextId
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load attack types');
    } finally {
      setLoading(false);
    }
  }, [tabValue, filterClass, createForm.scope]);

  useEffect(() => {
    loadAttackTypes();
  }, [loadAttackTypes]);

  // Reload attack types when tenant changes
  useEffect(() => {
    loadAttackTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  // Load tenants list for super admin tenant selection
  useEffect(() => {
    const loadTenants = async () => {
      if (isSuperAdmin) {
        try {
          const tenantsData = await api.getTenants();
          setTenants(tenantsData.map(t => ({ id: t.id, name: t.name })));
        } catch (err) {
          console.error('Failed to load tenants:', err);
        }
      }
    };
    loadTenants();
  }, [isSuperAdmin]);

  const handleCreateSubmit = async () => {
    try {
      setError(null);
      
      // Generate schema from selected fields (default) or use manual JSON schema (advanced override).
      const generatedSchema = generateSchemaFromFields();
      const schemaToSend =
        createSchemaMode === 'manual'
          ? (() => {
              try {
                const parsed = JSON.parse(createSchemaText || '');
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                  throw new Error('Schema JSON must be an object');
                }
                const normalized: Record<string, any> = { ...parsed };
                if (!normalized.type) normalized.type = 'object';
                if (!normalized.properties || typeof normalized.properties !== 'object' || Array.isArray(normalized.properties)) {
                  normalized.properties = {};
                }
                if (!Array.isArray(normalized.required)) normalized.required = [];
                return normalized;
              } catch (e) {
                const msg = e instanceof Error ? e.message : 'Invalid JSON';
                setCreateSchemaError(msg);
                throw new Error(`Invalid JSON schema: ${msg}`);
              }
            })()
          : generatedSchema;
      
      // Convert legacy targets to new format if targets field is selected
      let convertedFormData = { ...createFormData };
      if (convertedFormData.targets) {
        convertedFormData.targets = convertLegacyTargets(convertedFormData.targets);
      }
      
      // Create the final request with generated schema
      const requestData = {
        ...createForm,
        config_schema: schemaToSend
      };
      
      console.log('Creating attack type with data:', requestData);
      console.log('Schema:', schemaToSend);
      await api.createAttackType(requestData);
      handleCreateDialogClose();
      loadAttackTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create attack type');
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedAttackType) return;
    try {
      setError(null);
      
      // Generate schema from selected edit fields (default) or use manual JSON schema (advanced override).
      const generatedSchema = generateEditSchemaFromFields();
      const schemaToSend =
        editSchemaMode === 'manual'
          ? (() => {
              try {
                const parsed = JSON.parse(editSchemaText || '');
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                  throw new Error('Schema JSON must be an object');
                }
                const normalized: Record<string, any> = { ...parsed };
                if (!normalized.type) normalized.type = 'object';
                if (!normalized.properties || typeof normalized.properties !== 'object' || Array.isArray(normalized.properties)) {
                  normalized.properties = {};
                }
                if (!Array.isArray(normalized.required)) normalized.required = [];
                return normalized;
              } catch (e) {
                const msg = e instanceof Error ? e.message : 'Invalid JSON';
                setEditSchemaError(msg);
                throw new Error(`Invalid JSON schema: ${msg}`);
              }
            })()
          : generatedSchema;
      
      // Convert legacy targets to new format if targets field is selected
      let convertedFormData = { ...editFormData };
      if (convertedFormData.targets) {
        convertedFormData.targets = convertLegacyTargets(convertedFormData.targets);
      }
      
      // Create the final request with generated schema
      const requestData = {
        ...editForm,
        config_schema: schemaToSend
      };
      
      await api.updateAttackType(selectedAttackType.id, requestData);
      setEditDialogOpen(false);
      setSelectedAttackType(null);
      setEditFormData({});
      setEditSelectedFields({});
      setEditCustomFields([]);
      setEditSchemaMode('generated');
      setEditSchemaText('');
      setEditSchemaError(null);
      loadAttackTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update attack type');
    }
  };

  const handleEditClick = (attackType: AttackType) => {
    setSelectedAttackType(attackType);
    
    // Initialize edit form with attack type data
    setEditForm({
      name: attackType.name,
      description: attackType.description || '',
      attack_class: attackType.attack_class as 'application' | 'infrastructure' | 'volumetric',
      config_schema: attackType.config_schema
    });
    
    // Initialize edit field configuration from existing schema
    const templateFields = fieldTemplates[attackType.attack_class as keyof typeof fieldTemplates];
    const initialEditSelection: Record<string, any> = {};
    const editCustomFieldsList: Array<{name: string, type: string, title: string, description: string, required: boolean, config: any}> = [];
    
    // Check which template fields are used in the existing schema
    if (attackType.config_schema && attackType.config_schema.properties) {
      const existingProperties = attackType.config_schema.properties;
      const existingRequired = attackType.config_schema.required || [];
      
      // Check template fields
      templateFields.forEach(field => {
        if (existingProperties[field.name]) {
          initialEditSelection[field.name] = {
            ...field,
            selected: true,
            required: existingRequired.includes(field.name)
          };
        } else {
          initialEditSelection[field.name] = {
            ...field,
            selected: false,
            required: field.required
          };
        }
      });
      
      // Check for custom fields (fields not in template)
      Object.entries(existingProperties).forEach(([fieldName, fieldSchema]: [string, any]) => {
        const isTemplateField = templateFields.some(tf => tf.name === fieldName);
        if (!isTemplateField) {
          editCustomFieldsList.push({
            name: fieldName,
            type: fieldSchema.type || 'string',
            title: fieldSchema.title || fieldName,
            description: fieldSchema.description || '',
            required: existingRequired.includes(fieldName),
            config: fieldSchema
          });
        }
      });
    } else {
      // No existing schema, initialize with default template fields
      templateFields.forEach(field => {
        initialEditSelection[field.name] = {
          ...field,
          selected: field.required
        };
      });
    }
    
    setEditSelectedFields(initialEditSelection);
    setEditCustomFields(editCustomFieldsList);
    
    // Initialize form data with default values from schema
    const formData: Record<string, any> = {};
    if (attackType.config_schema?.properties) {
      Object.entries(attackType.config_schema.properties).forEach(([key, fieldSchema]: [string, any]) => {
        if (fieldSchema.default !== undefined) {
          formData[key] = fieldSchema.default;
        } else if (fieldSchema.type === 'array') {
          formData[key] = [];
        } else if (fieldSchema.type === 'object') {
          formData[key] = {};
        } else if (fieldSchema.type === 'boolean') {
          formData[key] = false;
        } else if (fieldSchema.type === 'number') {
          formData[key] = 0;
        } else {
          formData[key] = '';
        }
      });
    }
    setEditFormData(formData);
    setEditSchemaMode('generated');
    setEditSchemaError(null);
    setEditSchemaText(JSON.stringify(attackType.config_schema || {}, null, 2));
    setEditDialogOpen(true);
  };

  const handleViewClick = (attackType: AttackType) => {
    setSelectedAttackType(attackType);
    setViewDialogOpen(true);
  };

  const handleDeleteClick = (attackType: AttackType) => {
    setSelectedAttackType(attackType);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAttackType) return;
    
    try {
      setDeleteError(null);
      await api.deleteAttackType(selectedAttackType.id);
      setDeleteDialogOpen(false);
      setSelectedAttackType(null);
      setDeleteError(null);
      loadAttackTypes();
    } catch (err: any) {
      // Extract error message from API response
      let errorMessage = 'Failed to delete attack type';
      if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setDeleteError(errorMessage);
      // Don't close the dialog on error so user can see the error message
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeleteError(null);
    setSelectedAttackType(null);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    // Reset form with next available ID
    const nextId = findNextAvailableId(attackTypes, 'tenant');
    setCreateForm({
      name: '',
      description: '',
      attack_class: 'application',
      attack_numeric_id: nextId,
      config_schema: {
        type: 'object',
        properties: {},
        required: []
      },
      scope: 'tenant',
      tenant_ids: undefined
    });
    setCreateFormData({});
    setCreateStep(1);
    setAttackIdError(null);
    setCreateSchemaMode('generated');
    setCreateSchemaText('');
    setCreateSchemaError(null);
    setSelectedFields({});
    setCustomFields([]);
  };
 
  const handleAttackIdChange = (value: string) => {
    const numericValue = parseInt(value) || 0;
    setCreateForm(prev => {
      // Validate the attack ID based on current scope from prev state
      const error = validateAttackId(numericValue, prev.scope || 'tenant');
      setAttackIdError(error);
      
      return { ...prev, attack_numeric_id: numericValue };
    });
  };

  const handleClassChange = (attackClass: string) => {
    setCreateForm(prev => ({
      ...prev,
      attack_class: attackClass as 'application' | 'infrastructure' | 'volumetric'
    }));
    
    // Reset form data when class changes
    setCreateFormData({});
    
    // Initialize field selection with template fields
    const templateFields = fieldTemplates[attackClass as keyof typeof fieldTemplates];
    const initialSelection: Record<string, any> = {};
    templateFields.forEach(field => {
      initialSelection[field.name] = {
        ...field,
        selected: field.required // Auto-select required fields
      };
    });
    setSelectedFields(initialSelection);
    setCustomFields([]);
  };

  // Generate JSON schema from selected fields
  const generateSchemaFromFields = () => {
    const properties: Record<string, any> = {};
    const required: string[] = [];
    
    // Add selected template fields
    Object.entries(selectedFields).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.selected) {
        const { name, type, title, description, required: isRequired, selected: _selected, ...config } = fieldConfig;
        properties[name] = {
          type,
          title,
          description,
          ...config
        };
        if (isRequired) {
          required.push(name);
        }
      }
    });
    
    // Add custom fields
    customFields.forEach(field => {
      const { name, type, title, description, required: isRequired, config } = field;
      properties[name] = {
        type,
        title,
        description,
        ...config
      };
      if (isRequired) {
        required.push(name);
      }
    });
    
    return {
      type: 'object',
      properties,
      required
    };
  };

  // Generate JSON schema from edit fields
  const generateEditSchemaFromFields = () => {
    const properties: Record<string, any> = {};
    const required: string[] = [];
    
    // Add selected template fields
    Object.entries(editSelectedFields).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.selected) {
        const { name, type, title, description, required: isRequired, selected: _selected, ...config } = fieldConfig;
        properties[name] = {
          type,
          title,
          description,
          ...config
        };
        if (isRequired) {
          required.push(name);
        }
      }
    });
    
    // Add custom fields
    editCustomFields.forEach(field => {
      const { name, type, title, description, required: isRequired, config } = field;
      properties[name] = {
        type,
        title,
        description,
        ...config
      };
      if (isRequired) {
        required.push(name);
      }
    });
    
    return {
      type: 'object',
      properties,
      required
    };
  };

  // Keep the schema text area in sync while in "generated" mode.
  useEffect(() => {
    if (!createDialogOpen) return;
    if (createStep !== 3) return;
    if (createSchemaMode !== 'generated') return;
    try {
      setCreateSchemaText(JSON.stringify(generateSchemaFromFields(), null, 2));
      setCreateSchemaError(null);
    } catch (e) {
      setCreateSchemaError(e instanceof Error ? e.message : 'Failed to generate schema');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDialogOpen, createStep, createSchemaMode, selectedFields, customFields, createForm.attack_class]);

  useEffect(() => {
    if (!editDialogOpen) return;
    if (editSchemaMode !== 'generated') return;
    try {
      setEditSchemaText(JSON.stringify(generateEditSchemaFromFields(), null, 2));
      setEditSchemaError(null);
    } catch (e) {
      setEditSchemaError(e instanceof Error ? e.message : 'Failed to generate schema');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDialogOpen, editSchemaMode, editSelectedFields, editCustomFields, editForm.attack_class]);

  // Initialize template field selection on first open (preselect required fields).
  useEffect(() => {
    if (!createDialogOpen) return;
    const template = fieldTemplates[createForm.attack_class as keyof typeof fieldTemplates];
    if (!template) return;
    if (Object.keys(selectedFields).length > 0) return;
    const initialSelection: Record<string, any> = {};
    template.forEach((field) => {
      initialSelection[field.name] = { ...field, selected: field.required };
    });
    setSelectedFields(initialSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createDialogOpen]);

  // Helper functions for sorting and display
  const getScopeLabel = (attackType: AttackType) => {
    console.log('AttackType tenants:', attackType.tenants, 'Type:', typeof attackType.tenants);
    if (attackType.tenants === null || attackType.tenants === undefined || attackType.tenants.length === 0) {
      return 'Global';
    }
    return 'Tenant';
  };

  const getScopeColor = (attackType: AttackType) => {
    if (attackType.tenants === null || attackType.tenants === undefined || attackType.tenants.length === 0) {
      return 'primary';
    }
    return 'secondary';
  };

  // Extract numeric attack ID from the id field (format: "attack_id_{number}")
  // Supports both generic attack types (1-149) and custom attack types (150-999)
  const getAttackNumericId = (attackTypeId: string): number | null => {
    try {
      if (attackTypeId.startsWith('attack_id_')) {
        const numStr = attackTypeId.split('_')[2];
        const num = parseInt(numStr, 10);
        // Accept any numeric ID in the range 1-999 (includes both generic 1-149 and custom 150-999)
        if (!isNaN(num) && num >= 1 && num <= 999) {
          return num;
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort attack types
  const filteredAttackTypes = attackTypes
    .filter(type => type.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'attack_id':
          aValue = getAttackNumericId(a.id);
          bValue = getAttackNumericId(b.id);
          // Handle null values (put them at the end)
          if (aValue === null && bValue === null) return 0;
          if (aValue === null) return 1;
          if (bValue === null) return -1;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'attack_class':
          aValue = a.attack_class.toLowerCase();
          bValue = b.attack_class.toLowerCase();
          break;
        case 'scope':
          aValue = getScopeLabel(a).toLowerCase();
          bValue = getScopeLabel(b).toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }

      // Compare values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });


  if (permissionsLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Attack Types</Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Attack Type
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="All" />
            <Tab label="Tenant" />
            {isSuperAdmin && <Tab label="Global" />}
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Class</InputLabel>
              <Select
                value={filterClass}
                label="Filter by Class"
                onChange={(e) => setFilterClass(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                <MenuItem value="application">Application</MenuItem>
                <MenuItem value="infrastructure">Infrastructure</MenuItem>
                <MenuItem value="volumetric">Volumetric</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'attack_id'}
                      direction={sortField === 'attack_id' ? sortDirection : 'asc'}
                      onClick={() => handleSort('attack_id')}
                    >
                      Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'attack_class'}
                      direction={sortField === 'attack_class' ? sortDirection : 'asc'}
                      onClick={() => handleSort('attack_class')}
                    >
                      Class
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'scope'}
                      direction={sortField === 'scope' ? sortDirection : 'asc'}
                      onClick={() => handleSort('scope')}
                    >
                      Scope
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Linked Tenants</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'created_at'}
                      direction={sortField === 'created_at' ? sortDirection : 'asc'}
                      onClick={() => handleSort('created_at')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAttackTypes.map((attackType) => (
                  <TableRow key={attackType.id}>
                    <TableCell>
                      {getAttackNumericId(attackType.id) !== null ? (
                        <Typography variant="body2" fontWeight="medium">
                          {getAttackNumericId(attackType.id)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {attackType.name}
                        </Typography>
                        {attackType.description && (
                          <Typography variant="caption" color="text.secondary">
                            {attackType.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attackType.attack_class}
                        size="small"
                        color={attackType.attack_class === 'application' ? 'success' : 
                               attackType.attack_class === 'infrastructure' ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getScopeLabel(attackType)}
                        size="small"
                        color={getScopeColor(attackType) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {attackType.tenants === null || attackType.tenants === undefined || attackType.tenants.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          All Tenants
                        </Typography>
                      ) : attackType.tenant_details && attackType.tenant_details.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {attackType.tenant_details.map((tenant) => (
                            <Chip
                              key={tenant.id}
                              label={tenant.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {attackType.tenants?.length || 0} tenant(s)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatUiDateOnly(attackType.created_at, timezone)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleViewClick(attackType)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {canCreate && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditClick(attackType)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canCreate && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteClick(attackType)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Class</InputLabel>
              <Select
                value={filterClass}
                label="Filter by Class"
                onChange={(e) => setFilterClass(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                <MenuItem value="application">Application</MenuItem>
                <MenuItem value="infrastructure">Infrastructure</MenuItem>
                <MenuItem value="volumetric">Volumetric</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'attack_id'}
                      direction={sortField === 'attack_id' ? sortDirection : 'asc'}
                      onClick={() => handleSort('attack_id')}
                    >
                      Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'attack_class'}
                      direction={sortField === 'attack_class' ? sortDirection : 'asc'}
                      onClick={() => handleSort('attack_class')}
                    >
                      Class
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Linked Tenants</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'created_at'}
                      direction={sortField === 'created_at' ? sortDirection : 'asc'}
                      onClick={() => handleSort('created_at')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAttackTypes.map((attackType) => (
                  <TableRow key={attackType.id}>
                    <TableCell>
                      {getAttackNumericId(attackType.id) !== null ? (
                        <Typography variant="body2" fontWeight="medium">
                          {getAttackNumericId(attackType.id)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {attackType.name}
                        </Typography>
                        {attackType.description && (
                          <Typography variant="caption" color="text.secondary">
                            {attackType.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attackType.attack_class}
                        size="small"
                        color={attackType.attack_class === 'application' ? 'success' : 
                               attackType.attack_class === 'infrastructure' ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      {attackType.tenants === null || attackType.tenants === undefined || attackType.tenants.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      ) : attackType.tenant_details && attackType.tenant_details.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {attackType.tenant_details.map((tenant) => (
                            <Chip
                              key={tenant.id}
                              label={tenant.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {attackType.tenants?.length || 0} tenant(s)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatUiDateOnly(attackType.created_at, timezone)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleViewClick(attackType)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {canCreate && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditClick(attackType)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canCreate && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteClick(attackType)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {isSuperAdmin && (
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Class</InputLabel>
                <Select
                  value={filterClass}
                  label="Filter by Class"
                  onChange={(e) => setFilterClass(e.target.value)}
                >
                  <MenuItem value="">All Classes</MenuItem>
                  <MenuItem value="application">Application</MenuItem>
                  <MenuItem value="infrastructure">Infrastructure</MenuItem>
                  <MenuItem value="volumetric">Volumetric</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'attack_id'}
                        direction={sortField === 'attack_id' ? sortDirection : 'asc'}
                        onClick={() => handleSort('attack_id')}
                      >
                        Number
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'name'}
                        direction={sortField === 'name' ? sortDirection : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'attack_class'}
                        direction={sortField === 'attack_class' ? sortDirection : 'asc'}
                        onClick={() => handleSort('attack_class')}
                      >
                        Class
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Linked Tenants</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'created_at'}
                        direction={sortField === 'created_at' ? sortDirection : 'asc'}
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAttackTypes.map((attackType) => (
                    <TableRow key={attackType.id}>
                      <TableCell>
                        {getAttackNumericId(attackType.id) !== null ? (
                          <Typography variant="body2" fontWeight="medium">
                            {getAttackNumericId(attackType.id)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {attackType.name}
                          </Typography>
                          {attackType.description && (
                            <Typography variant="caption" color="text.secondary">
                              {attackType.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={attackType.attack_class}
                          size="small"
                          color={attackType.attack_class === 'application' ? 'success' : 
                                 attackType.attack_class === 'infrastructure' ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        {attackType.tenants === null || attackType.tenants === undefined || attackType.tenants.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            All Tenants
                          </Typography>
                        ) : attackType.tenant_details && attackType.tenant_details.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {attackType.tenant_details.map((tenant) => (
                              <Chip
                                key={tenant.id}
                                label={tenant.name}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {attackType.tenants?.length || 0} tenant(s)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatUiDateOnly(attackType.created_at, timezone)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => handleViewClick(attackType)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditClick(attackType)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {createStep === 1 && 'Step 1: Scope & Class'}
              {createStep === 2 && 'Step 2: Identity'}
              {createStep === 3 && 'Step 3: Schema'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {createStep} of 3
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {createStep === 1 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Choose the scope and class for your attack type.
              </Typography>
              <Grid container spacing={2}>
                {isSuperAdmin && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Scope</InputLabel>
                      <Select
                        value={createForm.scope}
                        label="Scope"
                        onChange={(e) => {
                          const newScope = e.target.value as 'tenant' | 'global';
                          // Find next available ID for the new scope
                          const nextId = findNextAvailableId(allAttackTypes, newScope);
                          setCreateForm(prev => ({ 
                            ...prev, 
                            scope: newScope,
                            attack_numeric_id: nextId,
                            // Clear tenant_ids when switching to global
                            tenant_ids: newScope === 'global' ? undefined : prev.tenant_ids
                          }));
                          // Revalidate the attack ID with new scope
                          const error = validateAttackId(nextId, newScope);
                          setAttackIdError(error);
                        }}
                      >
                        <MenuItem value="tenant">Tenant</MenuItem>
                        <MenuItem value="global">Global</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {isSuperAdmin && createForm.scope === 'tenant' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={tenants}
                      getOptionLabel={(option) => option.name}
                      value={tenants.filter(t => createForm.tenant_ids?.includes(t.id)) || []}
                      onChange={(_, newValue) => {
                        setCreateForm(prev => ({ 
                          ...prev, 
                          tenant_ids: newValue.map(v => v.id)
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Tenants"
                          placeholder="Choose tenants for this attack type"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            key={option.id}
                            size="small"
                          />
                        ))
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Attack Class</InputLabel>
                    <Select
                      value={createForm.attack_class}
                      label="Attack Class"
                      onChange={(e) => handleClassChange(e.target.value)}
                    >
                      <MenuItem value="application">Application</MenuItem>
                      <MenuItem value="infrastructure">Infrastructure</MenuItem>
                      <MenuItem value="volumetric">Volumetric</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {createStep === 2 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Provide a name and attack ID for your attack type.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    fullWidth
                    value={createForm.name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Attack ID
                    </Typography>
                    <Chip 
                      label="Auto-suggested" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                  <TextField
                    type="number"
                    fullWidth
                    value={createForm.attack_numeric_id}
                    onChange={(e) => handleAttackIdChange(e.target.value)}
                    inputProps={{ 
                      min: createForm.scope === 'global' ? 1 : 150, 
                      max: createForm.scope === 'global' ? 149 : 999 
                    }}
                    error={!!attackIdError}
                    helperText={
                      attackIdError || 
                      (createForm.scope === 'global' 
                        ? "Next available ID in range 1-149. Must be globally unique."
                        : "Next available ID in range 150-999. Must be globally unique.")
                    }
                    required
                  />
                  {createForm.scope === 'global' && 
                   createForm.attack_numeric_id >= 71 && 
                   createForm.attack_numeric_id <= 99 && 
                   createForm.attack_class !== 'volumetric' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Note:</strong> Attack IDs 71-99 are allocated for volumetric attack types. 
                        You can still use this ID for a {createForm.attack_class} attack type, but it's recommended 
                        to reserve this range for volumetric attacks.
                      </Typography>
                    </Alert>
                  )}
                  {createForm.scope === 'global' && 
                   createForm.attack_numeric_id >= 71 && 
                   createForm.attack_numeric_id <= 99 && 
                   createForm.attack_class === 'volumetric' && (
                    <Alert severity="success" sx={{ mt: 1, bgcolor: 'success.light' }}>
                      <Typography variant="body2">
                        ✓ This ID is in the recommended range (71-99) for volumetric attack types.
                      </Typography>
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          {createStep === 3 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Configure which fields users need to provide when creating scenarios with this attack type.
              </Typography>

              {/* Template Fields Selection */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Template Fields
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select from pre-defined fields for {createForm.attack_class} attacks:
                </Typography>
                
                <Grid container spacing={2}>
                  {fieldTemplates[createForm.attack_class as keyof typeof fieldTemplates].map((field) => (
                    <Grid item xs={12} sm={6} key={field.name}>
                      <Card 
                        sx={{ 
                          p: 2, 
                          cursor: 'pointer',
                          border: selectedFields[field.name]?.selected ? 2 : 1,
                          borderColor: selectedFields[field.name]?.selected ? 'primary.main' : 'divider',
                          bgcolor: selectedFields[field.name]?.selected ? 'primary.50' : 'background.paper'
                        }}
                        onClick={() => {
                          setSelectedFields(prev => ({
                            ...prev,
                            [field.name]: {
                              ...prev[field.name] || field,
                              selected: !prev[field.name]?.selected
                            }
                          }));
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Checkbox 
                            checked={selectedFields[field.name]?.selected || false}
                            onChange={() => {}}
                            size="small"
                          />
                          <Typography variant="subtitle2" sx={{ ml: 1 }}>
                            {field.title}
                            {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {field.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Type: {field.type}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Custom Fields */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Custom Fields
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newField = {
                        name: `custom_field_${customFields.length + 1}`,
                        type: 'string',
                        title: 'Custom Field',
                        description: 'Custom field description',
                        required: false,
                        config: {}
                      };
                      setCustomFields(prev => [...prev, newField]);
                    }}
                  >
                    Add Custom Field
                  </Button>
                </Box>
                
                {customFields.map((field, index) => (
                  <Card key={index} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Field Name"
                          value={field.name}
                          onChange={(e) => {
                            const updated = [...customFields];
                            updated[index].name = e.target.value;
                            setCustomFields(updated);
                          }}
                          size="small"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={field.type}
                            label="Type"
                            onChange={(e) => {
                              const updated = [...customFields];
                              updated[index].type = e.target.value;
                              setCustomFields(updated);
                            }}
                          >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="integer">Integer</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="boolean">Boolean</MenuItem>
                            <MenuItem value="array">Array</MenuItem>
                            <MenuItem value="object">Object</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Title"
                          value={field.title}
                          onChange={(e) => {
                            const updated = [...customFields];
                            updated[index].title = e.target.value;
                            setCustomFields(updated);
                          }}
                          size="small"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={field.required}
                              onChange={(e) => {
                                const updated = [...customFields];
                                updated[index].required = e.target.checked;
                                setCustomFields(updated);
                              }}
                            />
                          }
                          label="Required"
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setCustomFields(prev => prev.filter((_, i) => i !== index));
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </Box>

              {/* Schema Preview */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Generated Schema Preview
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={createSchemaMode === 'manual'}
                        onChange={(e) => {
                          const nextMode = e.target.checked ? 'manual' : 'generated';
                          setCreateSchemaMode(nextMode);
                          setCreateSchemaError(null);
                          if (nextMode === 'manual') {
                            setCreateSchemaText(JSON.stringify(generateSchemaFromFields(), null, 2));
                          }
                        }}
                      />
                    }
                    label="Edit JSON schema directly (advanced)"
                  />
                  {createSchemaMode === 'manual' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setCreateSchemaText(JSON.stringify(generateSchemaFromFields(), null, 2));
                        setCreateSchemaError(null);
                      }}
                    >
                      Reset To Generated
                    </Button>
                  )}
                </Box>
                {createSchemaMode === 'manual' && createSchemaError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {createSchemaError}
                  </Alert>
                )}
                <TextField
                  label="JSON Schema"
                  multiline
                  rows={8}
                  fullWidth
                  value={createSchemaMode === 'manual' ? createSchemaText : JSON.stringify(generateSchemaFromFields(), null, 2)}
                  onChange={(e) => {
                    if (createSchemaMode !== 'manual') return;
                    const nextText = e.target.value;
                    setCreateSchemaText(nextText);
                    try {
                      const parsed = JSON.parse(nextText || '');
                      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                        throw new Error('Schema JSON must be an object');
                      }
                      setCreateSchemaError(null);
                    } catch (err) {
                      setCreateSchemaError(err instanceof Error ? err.message : 'Invalid JSON');
                    }
                  }}
                  InputProps={{ readOnly: createSchemaMode !== 'manual' }}
                  size="small"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose}>Cancel</Button>
          {createStep > 1 && (
            <Button onClick={() => setCreateStep(createStep - 1)}>Previous</Button>
          )}
          {createStep < 3 ? (
            <Button variant="contained" onClick={() => setCreateStep(createStep + 1)}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateSubmit}
              disabled={!!attackIdError || (createSchemaMode === 'manual' && !!createSchemaError)}
            >
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Attack Type</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Attack Number"
                  fullWidth
                  value={selectedAttackType ? (getAttackNumericId(selectedAttackType.id) !== null ? getAttackNumericId(selectedAttackType.id) : 'N/A') : 'N/A'}
                  InputProps={{ readOnly: true }}
                  helperText="The numeric ID of this attack type (read-only)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  fullWidth
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Attack Class</InputLabel>
                  <Select
                    value={editForm.attack_class}
                    label="Attack Class"
                    onChange={(e) => setEditForm(prev => ({ ...prev, attack_class: e.target.value as any }))}
                  >
                    <MenuItem value="application">Application</MenuItem>
                    <MenuItem value="infrastructure">Infrastructure</MenuItem>
                    <MenuItem value="volumetric">Volumetric</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" sx={{ mb: 3 }}>
              Configure Required Fields
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select which fields users need to provide when creating scenarios with this attack type.
            </Typography>
            
            {/* Template Fields Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Template Fields
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select from pre-defined fields for {editForm.attack_class} attacks:
              </Typography>
              
              <Grid container spacing={2}>
                {fieldTemplates[editForm.attack_class as keyof typeof fieldTemplates].map((field) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <Card 
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        border: editSelectedFields[field.name]?.selected ? 2 : 1,
                        borderColor: editSelectedFields[field.name]?.selected ? 'primary.main' : 'divider',
                        bgcolor: editSelectedFields[field.name]?.selected ? 'primary.50' : 'background.paper'
                      }}
                      onClick={() => {
                        setEditSelectedFields(prev => ({
                          ...prev,
                          [field.name]: {
                            ...prev[field.name] || field,
                            selected: !prev[field.name]?.selected
                          }
                        }));
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Checkbox 
                          checked={editSelectedFields[field.name]?.selected || false}
                          onChange={() => {}}
                          size="small"
                        />
                        <Typography variant="subtitle2" sx={{ ml: 1 }}>
                          {field.title}
                          {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {field.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Type: {field.type}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Custom Fields */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Custom Fields
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const newField = {
                      name: `custom_field_${editCustomFields.length + 1}`,
                      type: 'string',
                      title: 'Custom Field',
                      description: 'Custom field description',
                      required: false,
                      config: {}
                    };
                    setEditCustomFields(prev => [...prev, newField]);
                  }}
                >
                  Add Custom Field
                </Button>
              </Box>
              
              {editCustomFields.map((field, index) => (
                <Card key={index} sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Field Name"
                        value={field.name}
                        onChange={(e) => {
                          const updated = [...editCustomFields];
                          updated[index].name = e.target.value;
                          setEditCustomFields(updated);
                        }}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={field.type}
                          label="Type"
                          onChange={(e) => {
                            const updated = [...editCustomFields];
                            updated[index].type = e.target.value;
                            setEditCustomFields(updated);
                          }}
                        >
                          <MenuItem value="string">String</MenuItem>
                          <MenuItem value="integer">Integer</MenuItem>
                          <MenuItem value="number">Number</MenuItem>
                          <MenuItem value="boolean">Boolean</MenuItem>
                          <MenuItem value="array">Array</MenuItem>
                          <MenuItem value="object">Object</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Title"
                        value={field.title}
                        onChange={(e) => {
                          const updated = [...editCustomFields];
                          updated[index].title = e.target.value;
                          setEditCustomFields(updated);
                        }}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.required}
                            onChange={(e) => {
                              const updated = [...editCustomFields];
                              updated[index].required = e.target.checked;
                              setEditCustomFields(updated);
                            }}
                          />
                        }
                        label="Required"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditCustomFields(prev => prev.filter((_, i) => i !== index));
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Box>

            {/* Schema Preview */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Generated Schema Preview
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editSchemaMode === 'manual'}
                      onChange={(e) => {
                        const nextMode = e.target.checked ? 'manual' : 'generated';
                        setEditSchemaMode(nextMode);
                        setEditSchemaError(null);
                        if (nextMode === 'manual') {
                          // Start from the currently loaded schema (usually the persisted one).
                          setEditSchemaText(JSON.stringify(selectedAttackType?.config_schema || generateEditSchemaFromFields(), null, 2));
                        }
                      }}
                    />
                  }
                  label="Edit JSON schema directly (advanced)"
                />
                {editSchemaMode === 'manual' && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEditSchemaText(JSON.stringify(generateEditSchemaFromFields(), null, 2));
                      setEditSchemaError(null);
                    }}
                  >
                    Reset To Generated
                  </Button>
                )}
              </Box>
              {editSchemaMode === 'manual' && editSchemaError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {editSchemaError}
                </Alert>
              )}
              <TextField
                label="JSON Schema"
                multiline
                rows={8}
                fullWidth
                value={editSchemaMode === 'manual' ? editSchemaText : JSON.stringify(generateEditSchemaFromFields(), null, 2)}
                onChange={(e) => {
                  if (editSchemaMode !== 'manual') return;
                  const nextText = e.target.value;
                  setEditSchemaText(nextText);
                  try {
                    const parsed = JSON.parse(nextText || '');
                    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                      throw new Error('Schema JSON must be an object');
                    }
                    setEditSchemaError(null);
                  } catch (err) {
                    setEditSchemaError(err instanceof Error ? err.message : 'Invalid JSON');
                  }
                }}
                InputProps={{ readOnly: editSchemaMode !== 'manual' }}
                size="small"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editSchemaMode === 'manual' && !!editSchemaError}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Attack Type Details</DialogTitle>
        <DialogContent>
          {selectedAttackType && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedAttackType.name}</Typography>
                  {selectedAttackType.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {selectedAttackType.description}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="medium">Class:</Typography>
                  <Chip
                    label={selectedAttackType.attack_class}
                    size="small"
                    color={selectedAttackType.attack_class === 'application' ? 'success' : 
                           selectedAttackType.attack_class === 'infrastructure' ? 'secondary' : 'primary'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="medium">Scope:</Typography>
                  <Chip
                    label={getScopeLabel(selectedAttackType)}
                    size="small"
                    color={getScopeColor(selectedAttackType) as any}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>Configuration Schema:</Typography>
                  <Paper variant="outlined" data-testid="attack-type-config-schema" sx={detailSurfaceSx}>
                    <pre style={{ margin: 0, fontSize: '0.875rem', fontFamily: 'monospace' }}>
                      {JSON.stringify(selectedAttackType.config_schema, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Attack Type</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">{deleteError}</Typography>
              </Alert>
            )}
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete the attack type "{selectedAttackType?.name}"?
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action cannot be undone. If this attack type is being used by any scenarios, 
                the deletion will fail and you'll need to delete or update those scenarios first.
              </Typography>
            </Alert>
            {selectedAttackType && (
              <Box data-testid="attack-type-details-panel" sx={detailSurfaceSx}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>Attack Type Details:</Typography>
                <Typography variant="body2">Name: {selectedAttackType.name}</Typography>
                <Typography variant="body2">Class: {selectedAttackType.attack_class}</Typography>
                <Typography variant="body2">Scope: {getScopeLabel(selectedAttackType)}</Typography>
                {selectedAttackType.description && (
                  <Typography variant="body2">Description: {selectedAttackType.description}</Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={!selectedAttackType}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttackTypes;
